// ReviewSubmitForm · client-facing form for /reviews page.
// Captures name, profession, photo, star rating and review body.
// Submits as multipart/form-data to POST /api/reviews. The new review
// is stored unapproved · admins must moderate it before it surfaces
// publicly. Uses sonner toast for user feedback.
import { useRef, useState } from "react";
import { Star, Upload, Send, Camera, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = ".jpg,.jpeg,.png,.webp";

export default function ReviewSubmitForm({ onSubmitted }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [profession, setProfession] = useState("");
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [body, setBody] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef(null);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error(t("comp.review.errPhotoSize"));
      e.target.value = "";
      return;
    }
    setPhoto(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const clearPhoto = () => {
    setPhoto(null);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !profession.trim() || !body.trim()) {
      toast.error(t("comp.review.errFillAll"));
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("profession", profession.trim());
      fd.append("rating", String(rating));
      fd.append("body", body.trim());
      if (photo) fd.append("photo", photo);
      await api.post("/reviews", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("commonToasts.reviewThanks", "Thank you · your review is queued for moderation"));
      setSubmitted(true);
      setName("");
      setProfession("");
      setBody("");
      setRating(5);
      clearPhoto();
      onSubmitted?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="review-submit-success"
        className="mt-16 max-w-2xl mx-auto card-beam p-10 text-center"
      >
        <div
          className="w-14 h-14 mx-auto mb-4 rounded-full grid place-items-center"
          style={{
            background:
              "color-mix(in srgb, var(--brand) 18%, transparent)",
            color: "var(--brand)",
          }}
        >
          <CheckCircle2 size={26} strokeWidth={1.7} />
        </div>
        <h3 className="font-display text-2xl text-text-1 font-medium mb-2">
          {t("comp.review.thankTitle")}
        </h3>
        <p className="text-text-3 text-[14px] font-light leading-relaxed mb-5">
          {t("comp.review.thankBody")}
        </p>
        <button
          type="button"
          onClick={() => setSubmitted(false)}
          className="text-[12px] underline text-text-2 hover:text-text-1"
          data-testid="review-submit-again"
        >
          {t("comp.review.submitAnother")}
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="review-submit-form"
      className="mt-16 max-w-3xl mx-auto card-beam p-7 lg:p-10"
    >
      <div className="text-center mb-7">
        <div className="pill-mono inline-flex mb-3">{t("comp.review.pill")}</div>
        <h3 className="font-display text-2xl lg:text-3xl text-text-1 font-medium tracking-[-0.02em] mb-2">
          {t("comp.review.title")} <span className="text-gradient-brand font-semibold">{t("comp.review.accent")}</span>
        </h3>
        <p className="text-text-3 text-[13.5px] font-light leading-relaxed max-w-xl mx-auto">
          {t("comp.review.subtitle")}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-[12px] uppercase tracking-[0.18em] text-text-3 mb-1.5 font-mono">
            {t("comp.review.fullName")}
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            required
            data-testid="review-input-name"
            placeholder={t("comp.review.fullNamePh")}
            className="w-full h-11 px-3.5 rounded-lg text-[14px] text-text-1 placeholder:text-text-3 focus:outline-none transition-colors"
            style={{
              background: "var(--surface-2)",
              border: "1px solid var(--line-strong)",
            }}
          />
        </div>
        <div>
          <label className="block text-[12px] uppercase tracking-[0.18em] text-text-3 mb-1.5 font-mono">
            {t("comp.review.profession")}
          </label>
          <input
            type="text"
            value={profession}
            onChange={(e) => setProfession(e.target.value)}
            maxLength={80}
            required
            data-testid="review-input-profession"
            placeholder={t("comp.review.professionPh")}
            className="w-full h-11 px-3.5 rounded-lg text-[14px] text-text-1 placeholder:text-text-3 focus:outline-none transition-colors"
            style={{
              background: "var(--surface-2)",
              border: "1px solid var(--line-strong)",
            }}
          />
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-[12px] uppercase tracking-[0.18em] text-text-3 mb-1.5 font-mono">
          {t("comp.review.rating")}
        </label>
        <div
          className="flex items-center gap-1.5"
          data-testid="review-input-rating"
        >
          {[1, 2, 3, 4, 5].map((n) => {
            const active = (hoverRating || rating) >= n;
            return (
              <button
                type="button"
                key={n}
                aria-label={t("comp.review.ratingStar", { count: n, n })}
                onClick={() => setRating(n)}
                onMouseEnter={() => setHoverRating(n)}
                onMouseLeave={() => setHoverRating(0)}
                data-testid={`review-star-${n}`}
                className="p-1 transition-transform hover:scale-110"
              >
                <Star
                  size={26}
                  strokeWidth={1.5}
                  className={active ? "fill-current" : ""}
                  style={{
                    color: active ? "var(--brand)" : "var(--text-3)",
                  }}
                />
              </button>
            );
          })}
          <span className="ml-2 text-[12px] text-text-3 font-mono">
            {rating} / 5
          </span>
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-[12px] uppercase tracking-[0.18em] text-text-3 mb-1.5 font-mono">
          {t("comp.review.yourReview")}
        </label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={1500}
          required
          rows={5}
          data-testid="review-input-body"
          placeholder={t("comp.review.yourReviewPh")}
          className="w-full px-3.5 py-3 rounded-lg text-[14px] text-text-1 placeholder:text-text-3 focus:outline-none transition-colors resize-y leading-relaxed"
          style={{
            background: "var(--surface-2)",
            border: "1px solid var(--line-strong)",
          }}
        />
        <div className="text-right text-[12px] text-text-3 font-mono mt-1 tabular-nums">
          {body.length} / 1500
        </div>
      </div>

      <div className="mb-5">
        <label className="block text-[12px] uppercase tracking-[0.18em] text-text-3 mb-1.5 font-mono">
          {t("comp.review.photoOpt")}
        </label>
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          onChange={onPickFile}
          data-testid="review-input-photo"
          className="hidden"
        />
        {photoPreview ? (
          <div
            className="flex items-center gap-3 p-3 rounded-lg"
            style={{
              background: "var(--surface-2)",
              border: "1px solid var(--line-strong)",
            }}
          >
            <img
              src={photoPreview}
              alt={t("comp.review.preview")}
              className="w-14 h-14 rounded-full object-cover"
              style={{ border: "1px solid var(--line)" }}
            />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-text-1 truncate">
                {photo?.name}
              </div>
              <div className="text-[12px] text-text-3 font-mono">
                {(photo?.size / 1024).toFixed(0)} KB · {t("comp.review.readyUpload")}
              </div>
            </div>
            <button
              type="button"
              onClick={clearPhoto}
              data-testid="review-photo-remove"
              className="text-text-3 hover:text-text-1 p-1.5"
              aria-label={t("comp.review.removePhoto")}
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            data-testid="review-photo-pick"
            className="w-full h-14 inline-flex items-center justify-center gap-2 rounded-lg text-[13px] text-text-2 hover:text-text-1 transition-colors"
            style={{
              background: "var(--surface-2)",
              border: "1px dashed var(--line-strong)",
            }}
          >
            <Camera size={15} strokeWidth={1.7} />
            <span>{t("comp.review.addPhoto")}</span>
            <span className="text-[12px] text-text-3 font-mono ml-2">
              {t("comp.review.photoFormats")}
            </span>
          </button>
        )}
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="review-submit-btn"
        className="btn-solid-brand inline-flex items-center justify-center gap-2 !h-12 w-full sm:w-auto !px-6"
        style={{ opacity: submitting ? 0.6 : 1 }}
      >
        {submitting ? (
          <>
            <Upload size={14} className="animate-pulse" /> {t("comp.review.sending")}
          </>
        ) : (
          <>
            <Send size={14} /> {t("comp.review.submitReview")}
          </>
        )}
      </button>
    </form>
  );
}
